'use client';

import * as React from 'react';
import { Search, Package, User, Calendar, DollarSign } from 'lucide-react';
import { format } from 'date-fns';

import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrdersList } from '@/lib/api/orders';
import { formatMoney } from '@/lib/money';
import type { PickedOrder } from './order-autofill';

interface OrderResourcePickerProps {
  storeId: string;
  value: string;
  onChange: (order: PickedOrder) => void;
}

export function OrderResourcePicker({ storeId, value, onChange }: OrderResourcePickerProps) {
  const [searchQuery, setSearchQuery] = React.useState('');

  const { data: ordersData, isLoading } = useOrdersList({
    sortBy: { field: 'orders.createdAt', direction: 'desc' },
    pagination: { page: 0, pageSize: 10 },
    search: searchQuery || undefined,
    filters: {
      'stores.id': [storeId],
    },
  });

  const orders = ordersData?.orders || [];

  return (
    <div className="w-full space-y-3">
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search orders by ID or customer..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-9 pl-9"
        />
      </div>

      <div className="max-h-[300px] space-y-2 overflow-y-auto p-1">
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <Card className="p-6">
            <div className="flex flex-col items-center text-center">
              <Package className="text-muted-foreground mb-2 h-8 w-8" />
              <p className="text-sm font-medium">No orders found</p>
            </div>
          </Card>
        ) : (
          orders.map((order: any) => (
            <button
              key={order.id}
              onClick={() => onChange(order)}
              className={`bg-card hover:bg-accent w-full rounded-md border p-3 text-left transition-colors ${
                value === order.id ? 'ring-primary bg-accent ring-2' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="bg-muted flex h-10 w-10 items-center justify-center rounded">
                  <Package className="text-muted-foreground h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">Order #{order.orderNumber || order.id.slice(-8)}</p>
                    <Badge variant="outline" className="text-xs">
                      {order.status}
                    </Badge>
                  </div>
                  {order.customer && (
                    <p className="text-muted-foreground truncate text-xs">
                      <User className="mr-1 inline h-3 w-3" />
                      {order.customer.email}
                    </p>
                  )}
                  <div className="text-muted-foreground flex items-center gap-3 text-xs">
                    {order.summaries?.[0]?.totalAdjustedAmount && (
                      <span>
                        <DollarSign className="mr-1 inline h-3 w-3" />
                        {formatMoney(order.summaries[0].totalAdjustedAmount, order.currency || 'USD')}
                      </span>
                    )}
                    {order.createdAt && (
                      <span>
                        <Calendar className="mr-1 inline h-3 w-3" />
                        {format(new Date(order.createdAt), 'MMM dd, yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

